import 'react-datepicker/dist/react-datepicker.css'
import './MonthPicker.css'

import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import { MonthPickerHeader } from './MonthPickerHeader'
import { getFullMonth, getLastOneMonth } from './monthPickerUtils'

export interface DateInterval {
  start: Date
  end: Date
}

export interface DatePeriod {
  start: string
  end: string
}

export interface MonthPickerProps {
  placeholder?: string
  className?: string
  disabled?: boolean
  maxDate?: Date | null
  minDate?: Date | null
  scrollableYearDropdown?: boolean
  dateFormat?: string
  onChange: (value: DatePeriod | null) => void
  overrideLastMonthForTesting?: boolean
}

export function MonthPicker({
  placeholder,
  onChange,
  dateFormat,
  overrideLastMonthForTesting,
  ...props
}: MonthPickerProps) {
  const [value, setValue] = useState<Date | null>(null)

  const [isLastThirtyDaysSelected, setIsLastThirtyDaysSelected] = useState(true)
  const startRef = useRef<ReactDatePicker>(null)

  const onMonthChange = (date: Date) => {
    setValue(date)
    setIsLastThirtyDaysSelected(false)
    onChange(getFullMonth(date))
  }

  const onClickLastThirtyDays = () => {
    setValue(null)
    setIsLastThirtyDaysSelected(true)
    onChange(getLastOneMonth())
    startRef?.current?.setOpen(false)
  }

  useEffect(() => {
    overrideLastMonthForTesting
      ? onChange({ start: '2022-12-01T03:00:00.000Z', end: '2022-12-31T03:00:00.000Z' })
      : onChange(getLastOneMonth())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='date-month-picker-root'>
      <ReactDatePicker
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={startRef as any}
        selected={value}
        showPopperArrow={false}
        dateFormat={dateFormat ?? 'MMM yyyy'}
        customInput={<CustomInputSelect isLastThirtyDaysSelected={isLastThirtyDaysSelected} />}
        calendarClassName='shadow-xl ring-1 ring-black ring-opacity-5 p-6 border-0 rounded-md'
        renderCustomHeader={(props) => (
          <MonthPickerHeader
            onClickLastThirtyDays={onClickLastThirtyDays}
            isLastThirtyDaysSelected={isLastThirtyDaysSelected}
            {...props}
          />
        )}
        placeholderText={placeholder}
        onChange={onMonthChange}
        disabledKeyboardNavigation
        showMonthYearPicker
        {...props}
      />
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  isLastThirtyDaysSelected?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, isLastThirtyDaysSelected, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <div
      className='mt-1 flex w-36 cursor-pointer items-center justify-between rounded-md border border-brand-gray-300 bg-white px-3 py-2 font-inter text-sm text-brand-gray-900 shadow-sm'
      onClick={() => {
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <button ref={innerRef} {...props}>
        {isLastThirtyDaysSelected ? 'Last 30 days' : value}
      </button>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-brand-gray-400' aria-hidden='true' />
    </div>
  )
})
